// Schrijft een selectieplan (zie selectionPlan.js) weg: rijen aanmaken, zacht
// verwijderde rijen terugzetten, en rijen loslaten. In batches, met voortgang.
//
// Nodig omdat Detail.jsx per adres één write deed. Een hele wijk in Zutphen is
// 3.550 adressen; Firestore weigert een batch boven de 500.
//
// Loslaten gaat hier ook langs en NIET via de gedeelde softDeleteDocs. Die is
// voor één ding dat uit veel documenten bestaat: hij registreert elk document
// in DeletedItems en maakt het eerste de ouder van de rest, zodat de
// prullenbak alles in één keer terugzet. Rijen van een wervingsactie zijn
// onafhankelijk; daar bracht het terugzetten van één rij uit de prullenbak de
// hele losgelaten wijk mee, en bij opnieuw kiezen bleven de aantekeningen
// staan. De prullenbak vindt de rijen ook zonder aantekening: hij leest de
// collectie zelf (fetchTrashItems), en restoreDeletedItem werkt zonder.
// AI-CONTEXT: See docs/pitfalls.md#database-batch-soft-delete-helper-turns-independent-rows-into-one-thing
//
// Mislukt een batch, dan stopt het en komt terug hoeveel er wél staan. Opnieuw
// proberen maakt een nieuw plan uit de actuele rijen, en vult dus alleen het gat.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)
// AI-CONTEXT: See docs/patterns.md#data-clone-with-children (batches van 400)

import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { EMPTY_REGISTRATIE } from './registratieOptions'

const COLLECTION = 'Wervingsregistraties'
const BATCH_SIZE = 400

/**
 * @typedef {object} SchrijfResultaat
 * @property {number} written - Hoeveel rijen er zijn aangemaakt of teruggezet
 * @property {number} total - Hoeveel het er hadden moeten zijn
 * @property {Error | null} error - De fout waarop het stopte, of null
 */

/**
 * Een lege registratierij voor één adres, zoals Detail.jsx hem altijd maakte.
 *
 * @param {object} adres - Adres uit PDOK (zie buildLocationFromPdokDoc)
 * @param {object} context
 * @param {string} context.wervingsactieId - ID van de wervingsactie
 * @param {number} context.position - Plaats in de lijst
 * @param {string} context.id - Nieuw ID voor de rij
 * @returns {object}
 */
const buildRegistrationDoc = (adres, { wervingsactieId, position, id }) => ({
    ID: id,
    WervingsactieID: wervingsactieId,
    CompagnyID: client,
    Compagny: client,
    Position: position,
    Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
    Key: adres.Key,
    BAGid: adres.BAGid || '',
    Postcode: adres.Postcode || '',
    Huisnummer: adres.Huisnummer || '',
    HuisnummerToevoeging: adres.HuisnummerToevoeging || '',
    Straatnaam: adres.Straatnaam || '',
    Woonplaats: adres.Woonplaats || '',
    BuurtCode: adres.BuurtCode || '',
    WijkCode: adres.WijkCode || '',
    Lat: adres.Lat ?? null,
    Lng: adres.Lng ?? null,
    ...EMPTY_REGISTRATIE,
    Deleted: false,
})

// Terugzetten zoals restoreDeletedItem het doet: de velden weg, niet op false.
// Het veldwerk in de rij blijft staan.
const reactivatePatch = () => ({
    Deleted: firebase.firestore.FieldValue.delete(),
    DeletedAt: firebase.firestore.FieldValue.delete(),
    DeletedBy: firebase.firestore.FieldValue.delete(),
    Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
})

/**
 * Voert schrijfacties uit in batches van BATCH_SIZE. Stopt bij de eerste
 * mislukte batch en zegt hoeveel er tot dan gelukt zijn.
 *
 * @param {Array<(batch: object) => void>} operations - Elk legt één schrijfactie in de batch
 * @param {(written: number, total: number) => void} [onProgress] - Na elke geslaagde batch
 * @returns {Promise<SchrijfResultaat>}
 */
const commitInBatches = async (operations, onProgress) => {
    const total = operations.length
    let written = 0

    for (let i = 0; i < total; i += BATCH_SIZE) {
        const chunk = operations.slice(i, i + BATCH_SIZE)
        const batch = db.batch()
        chunk.forEach((apply) => apply(batch))

        try {
            await batch.commit()
        } catch (error) {
            // Niet doorgooien: de aanroeper moet kunnen zeggen hoeveel er al
            // staan. Zonder dat leest een half gelukte wijk als 'niets gebeurd'.
            return { written, total, error }
        }

        written += chunk.length
        if (onProgress) onProgress(written, total)
    }

    return { written, total, error: null }
}

/**
 * @param {object} params
 * @param {{ toCreate: Array<object>, toReactivate: Array<{ docid: string }> }} params.plan - Uit planSelect
 * @param {string} params.wervingsactieId - ID van de wervingsactie
 * @param {number} params.firstPosition - Positie van de eerste nieuwe rij
 * @param {(written: number, total: number) => void} [params.onProgress] - Na elke geslaagde batch
 * @returns {Promise<SchrijfResultaat>}
 */
const writeSelection = ({ plan, wervingsactieId, firstPosition, onProgress }) => commitInBatches([
    ...plan.toCreate.map((adres, i) => (batch) => batch.set(
        db.collection(COLLECTION).doc(),
        buildRegistrationDoc(adres, { wervingsactieId, position: firstPosition + i, id: uuid() }),
    )),
    ...plan.toReactivate.map((rij) => (batch) => batch.update(
        db.collection(COLLECTION).doc(rij.docid),
        reactivatePatch(),
    )),
], onProgress)

/**
 * Laat rijen los: zacht verwijderen (ADR-008), in batches, met voortgang.
 *
 * @param {object} params
 * @param {{ toSoftDelete: Array<{ docid: string }> }} params.plan - Uit planDeselect
 * @param {string} [params.authId] - Wie loslaat
 * @param {(written: number, total: number) => void} [params.onProgress] - Na elke geslaagde batch
 * @returns {Promise<SchrijfResultaat>}
 */
const writeDeselection = ({ plan, authId, onProgress }) => commitInBatches(
    plan.toSoftDelete.map((rij) => (batch) => batch.update(
        db.collection(COLLECTION).doc(rij.docid),
        {
            Deleted: true,
            DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
            DeletedBy: authId || '',
        },
    )),
    onProgress,
)

export { buildRegistrationDoc, writeDeselection, writeSelection }
